import { useDispatch } from "react-redux";
import {
  type frontRoutes,
  getJwtExpiredSinceInSeconds,
  handleJWTStringPossiblyContainingJsonError,
} from "shared";
import { RenewExpiredJwtButton } from "src/app/components/auth/RenewExpiredJwtButton";
import { FullPageFeedback } from "src/app/components/feedback/FullpageFeedback";
import { WithFeedbackReplacer } from "src/app/components/feedback/WithFeedbackReplacer";
import { ErrorPage } from "src/app/pages/error/ErrorPage";
import { frontErrors } from "src/app/pages/error/front-errors";
import { makeUseTypedRoute } from "src/app/routes/routes.hooks";
import { loginIllustration } from "src/assets/img/illustrations";
import { authSlice } from "src/core-logic/domain/auth/auth.slice";
import type { FeedbackTopic } from "src/core-logic/domain/feedback/feedback.content";

const useMagicLinkInterstitialRoute =
  makeUseTypedRoute<(typeof frontRoutes.magicLinkInterstitial)["name"]>();

export const MagicLinkInterstitialPage = () => {
  const { params } = useMagicLinkInterstitialRoute(["magicLinkInterstitial"]);
  const { code, state, email } = params;
  const dispatch = useDispatch();
  const expiredSinceSeconds = getJwtExpiredSinceInSeconds(code, new Date());
  const feedbackTopic: FeedbackTopic = "renew-expired-jwt";
  const RenewJwtButton = (
    <RenewExpiredJwtButton
      key={RenewExpiredJwtButton.name}
      expiredJwt={code}
      feedbackTopic={feedbackTopic}
      state={state}
    />
  );

  if (expiredSinceSeconds) {
    throw frontErrors.auth.expiredJwt({ RenewJwtButton });
  }

  return (
    <WithFeedbackReplacer
      topic="magic-link-interstitial"
      renderFeedback={({ title, message }) => {
        const messageText = handleJWTStringPossiblyContainingJsonError(message);
        return (
          <ErrorPage
            title={title ?? "Erreur de connexion"}
            buttons={[RenewJwtButton]}
            error={{
              message: messageText,
              name: title ?? "Erreur de connexion",
            }}
          />
        );
      }}
    >
      <FullPageFeedback
        title="Connexion à Immersion Facilitée"
        illustration={loginIllustration}
        content={
          <p>
            Vous êtes en train de vous connecter à Immersion Facilitée via le
            compte <strong>{email}</strong>, souhaitez-vous continuer ?
          </p>
        }
        buttons={[
          {
            children: "Oui, me connecter à Immersion Facilitée",
            onClick: () => {
              dispatch(
                authSlice.actions.confirmLoginByMagicLinkRequested({
                  code,
                  state,
                  email,
                  feedbackTopic: "magic-link-interstitial",
                }),
              );
            },
          },
        ]}
      />
    </WithFeedbackReplacer>
  );
};
